import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { API } from '../../../../constants';
import { useTrans } from '../../../../services/i18n';
import RegionSelector from '../../../../components/generics/SearchTopPanel/FiltersMenu/Filters/RegionSelector';
import { CustomizableAutocomplete } from '../../../../components/autocompletes/CusomizableAutocomplete';
import { getAllowedStateCodes, reconcileStatesOnRegionChange } from '../../../../utils/regionUtils';

const headingStyle = {
  color: 'black',
  fontSize: 15,
  fontFamily: 'Lexend',
  fontWeight: '500',
  wordWrap: 'break-word',
};

/**
 * "Areas Served" section for the Industry step.
 *
 * Regions are transient UI state held locally — they populate and limit the
 * States list but are never emitted upward or persisted. Only the computed
 * `states` list leaves the component via `onChange`, which is what guarantees
 * regions never reach `finalData`.
 */
export default function AreasServed({ states = [], onChange }) {
  const { trans } = useTrans();
  const [regions, setRegions] = useState([]);

  const allowedStateCodes = useMemo(() => getAllowedStateCodes(regions), [regions]);

  const emit = (nextStates) => {
    if (typeof onChange === 'function') {
      onChange(nextStates);
    }
  };

  const handleRegionsChange = (nextRegions) => {
    const nextStates = reconcileStatesOnRegionChange({
      prevRegions: regions,
      nextRegions,
      selectedStates: states,
    });
    setRegions(nextRegions);
    emit(nextStates);
  };

  const handleStatesChange = (event) => {
    const nextStates = Array.isArray(event?.target?.value) ? event.target.value : [];
    emit(nextStates);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <div style={headingStyle}>{trans('organization-form-areas-served')}</div>
      </Grid>

      <Grid item xs={12}>
        <RegionSelector
          value={regions}
          onChange={handleRegionsChange}
          helperText={trans('region-states-auto-added-hint')}
          limitedHelperText={trans('region-state-selection-limited-hint')}
          showLimitedHint={regions.length > 0}
        />
      </Grid>

      <Grid item xs={12}>
        <CustomizableAutocomplete
          apiMethod={API.UTILITIES.GET_STATES}
          label={trans('organization-form-states')}
          placeholder={trans('organization-form-states-placeholder')}
          multi
          value={states}
          allowedStateCodes={allowedStateCodes}
          onChange={handleStatesChange}
        />
      </Grid>
    </Grid>
  );
}

AreasServed.propTypes = {
  states: PropTypes.array,
  onChange: PropTypes.func,
};
